import dayjs from 'dayjs';
import { type ApiResponse } from '@/api/core/_models';
import { createMockTickets, MOCK_TICKET_CATALOG } from './_mock';
import {
  type Ticket,
  type TicketDepartment,
  type TicketEvent,
  TicketEventKind,
  type TicketOpenInput,
  TicketSla,
  type TicketSlaDeadline,
  TicketStatus,
} from './_models';
import { ticketDueAt } from './_mockDeadlines';
import { dueWeekdayLabel, findSubjectPath, ticketTitle } from './_utils';

function resolveMockResponse<T>(data: T): Promise<ApiResponse<T>> {
  const responseData = structuredClone(data);
  const response: ApiResponse<T> = { success: true, status: 200, data: responseData, length: 1 };
  return Promise.resolve(response);
}

function rejectMockResponse(status: number, message: string): Promise<never> {
  const error = new Error(message);
  const responseError = Object.assign(error, { status });
  return Promise.reject(responseError);
}

function getCatalog(): Promise<ApiResponse<TicketDepartment[]>> {
  return resolveMockResponse(MOCK_TICKET_CATALOG);
}

export function createMockTicketRequests(now: () => Date = () => new Date()) {
  const tickets: Ticket[] = createMockTickets();

  function formatEventTime() {
    const currentDate = now();
    return dayjs(currentDate).format('[hoje,] HH:mm');
  }

  function listTickets() {
    return resolveMockResponse(tickets);
  }

  function getTicket(id: string): Promise<ApiResponse<Ticket>> {
    const ticket = tickets.find((item) => item.id === id);
    if (!ticket) return rejectMockResponse(404, 'Este chamado não existe.');
    return resolveMockResponse(ticket);
  }

  function replyToTicket(id: string, text: string): Promise<ApiResponse<Ticket>> {
    const ticket = tickets.find((item) => item.id === id);
    if (!ticket) return rejectMockResponse(404, 'Este chamado não existe.');
    if (ticket.status === TicketStatus.RESOLVED) return rejectMockResponse(409, 'Este chamado está encerrado.');
    const replyText = text.trim();
    if (!replyText) return rejectMockResponse(400, 'Escreva uma resposta antes de enviar.');
    const replyTime = formatEventTime();
    const reply: TicketEvent = {
      id: `${ticket.number}-${ticket.events.length + 1}`,
      kind: TicketEventKind.MESSAGE,
      author: ticket.requester,
      role: 'você',
      mine: true,
      at: replyTime,
      text: replyText,
      attachments: [],
    };
    ticket.events.push(reply);
    if (ticket.status === TicketStatus.WAITING_STORE) {
      const backToTeam: TicketEvent = {
        id: `${ticket.number}-${ticket.events.length + 1}`,
        kind: TicketEventKind.STATUS_CHANGED,
        author: 'Sistema',
        role: 'sistema',
        mine: false,
        at: replyTime,
        text: `${ticket.path.department} recebeu sua resposta`,
        attachments: [],
        status: TicketStatus.IN_PROGRESS,
      };
      ticket.events.push(backToTeam);
      ticket.status = TicketStatus.IN_PROGRESS;
    }
    ticket.lastUpdate = 'Você respondeu';
    ticket.lastUpdateAt = replyTime;
    return resolveMockResponse(ticket);
  }

  function openTicket(input: TicketOpenInput): Promise<ApiResponse<Ticket>> {
    const subjectPath = findSubjectPath(MOCK_TICKET_CATALOG, input.subjectId);
    if (!subjectPath) return rejectMockResponse(400, 'Escolha um assunto.');
    const title = ticketTitle(subjectPath.subject, subjectPath.form, input.fields);
    const ticketNumbers = tickets.map((item) => item.number);
    const highestNumber = Math.max(...ticketNumbers);
    const number = highestNumber + 1;
    const openedAt = formatEventTime();
    const openedDate = now();
    const dueAt = ticketDueAt(subjectPath.sla, openedDate);
    const dueLabel = dayjs(dueAt).format('DD/MM, HH:mm');
    const dueWeekday = dueWeekdayLabel(dueAt);
    const openedText = [input.description, input.conversation].filter(Boolean).join('\n\n');
    const openedEvent: TicketEvent = {
      id: `${number}-1`,
      kind: TicketEventKind.OPENED,
      author: input.requester,
      role: 'você',
      mine: true,
      at: openedAt,
      text: openedText,
      attachments: input.attachments,
    };
    const autoReplyEvent: TicketEvent = {
      id: `${number}-2`,
      kind: TicketEventKind.AUTO_REPLY,
      author: 'Resposta automática',
      role: 'sistema',
      mine: false,
      at: openedAt,
      text: `Recebemos seu pedido. A equipe de ${subjectPath.department} confere e responde até ${dueWeekday}.`,
      attachments: [],
    };
    const ticket: Ticket = {
      id: String(number),
      number,
      title,
      status: TicketStatus.NEW,
      path: {
        subjectId: subjectPath.subjectId,
        department: subjectPath.department,
        category: subjectPath.category,
        subject: subjectPath.subject,
      },
      sla: subjectPath.sla,
      dueLabel,
      dueAt,
      late: false,
      store: input.store,
      requester: input.requester,
      openedAt,
      origin: input.origin,
      relatedTicketNumber: input.relatedTicketNumber,
      fields: input.fields,
      events: [openedEvent, autoReplyEvent],
      lastUpdate: 'Aberto por você',
      lastUpdateAt: openedAt,
    };
    tickets.unshift(ticket);
    return resolveMockResponse(ticket);
  }

  function listSlaDeadlines(): Promise<ApiResponse<TicketSlaDeadline[]>> {
    const currentDate = now();
    const slas = Object.values(TicketSla);
    const deadlines = slas.map((sla) => ({ sla, dueAt: ticketDueAt(sla, currentDate) }));
    return resolveMockResponse(deadlines);
  }

  return { listTickets, getTicket, replyToTicket, openTicket, getCatalog, listSlaDeadlines };
}
